'use client'

import { Box, Typography } from '@mui/material'
import { Icon } from '@media-components'
import { getStyles } from '@utils/styles'
import defaultStyles from './styles'

export type ResourceWhatWeDoItemData = {
  title: string
  description?: string
  link?: string
}

export type ResourceWhatWeDoSectionData = {
  title?: string
  items?: ResourceWhatWeDoItemData[]
}

type Props = ResourceWhatWeDoSectionData

export function WhatWeDoSection({
  title = 'How we can help',
  items = [],
}: Props) {
  const styles = getStyles(defaultStyles)
  const columnCount = Math.min(Math.max(items.length, 1), 4)

  if (!items.length) return null

  return (
    <Box id="what-we-do" component="section" {...styles('section')}>
      <Icon
        iconName="MessageQuestion"
        colorValues="text.primary"
        fontSize="large"
        currentValue={false}
        sx={{
          fill: 'none',
          '& path': {
            fill: 'none !important',
            stroke: 'currentColor',
          },
        }}
      />
      <Typography component="h2" variant="h6.700" color="text.primary">
        {title}
      </Typography>

      <Box
        {...styles('grid', {
          gridTemplateColumns: {
            xs: '1fr',
            sm: columnCount > 1 ? '1fr 1fr' : '1fr',
            md: `repeat(${columnCount}, 1fr)`,
          },
        })}
      >
        {items.map((item) => {
          const hasLink = Boolean(item.link)

          return (
            <Box
              key={item.title}
              {...styles(hasLink ? ['item', 'itemLink'] : 'item')}
              {...(hasLink
                ? {
                    component: 'a' as const,
                    href: item.link,
                    target: item.link?.startsWith('http')
                      ? '_blank'
                      : undefined,
                    rel: item.link?.startsWith('http')
                      ? 'noopener noreferrer'
                      : undefined,
                  }
                : {})}
            >
              <Typography variant="body1.600" color="text.primary">
                {item.title}
              </Typography>
              {item.description && (
                <Typography
                  variant="body3.400"
                  color="text.secondary"
                  mt={0.75}
                  display="block"
                >
                  {item.description}
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
